import { AppModule } from '@/store/modules/app';

/**
 * Helpers around the native Constraint Validation API. They only decide WHAT
 * a field should say; wiring them to form events is done by the
 * `v-native-validation` directive (`src/directives/nativeValidation.ts`).
 */

export type ValidatableField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/**
 * A form-specific rule the browser cannot express with attributes (e.g. "both
 * passwords match"). Returns the error text for the field, or '' when it passes.
 */
export type FieldValidator = (field: ValidatableField) => string;

/** Translation id of `Bitte füllen Sie das Feld "%s(field_label)" aus.` */
export const REQUIRED_FIELD_MESSAGE_ID = 16305;

/** Translation id of `Bitte halten Sie sich an das erforderliche Format.` */
export const FORMAT_MESSAGE_ID = 16523;

/** Translation id of `Bitte geben Sie eine gültige E-Mail-Adresse ein.` */
export const EMAIL_MESSAGE_ID = 4207;

const FIELD_LABEL_PLACEHOLDER = '%s(field_label)';

export function isValidatableField(target: EventTarget | null): target is ValidatableField {
  return target instanceof HTMLInputElement
    || target instanceof HTMLSelectElement
    || target instanceof HTMLTextAreaElement;
}

/**
 * The name a user knows the field by, in the same order a screen reader would
 * pick it: an explicit override, the associated <label>, `aria-label`, and
 * finally the placeholder - the registration forms have no visible labels and
 * show the field name only as a placeholder.
 */
export function fieldLabel(field: ValidatableField): string {
  const explicit = field.dataset.fieldLabel;
  if (explicit) return explicit;

  const label = field.labels?.[0]?.textContent?.trim();
  if (label) return label;

  return field.getAttribute('aria-label')
    || field.getAttribute('placeholder')
    || field.name
    || '';
}

function translation(id: number): string {
  return (AppModule.translation as Record<number, string>)[id] || '';
}

export function requiredFieldMessage(label: string): string {
  return translation(REQUIRED_FIELD_MESSAGE_ID).replace(FIELD_LABEL_PLACEHOLDER, label);
}

export function formatMessage(): string {
  return translation(FORMAT_MESSAGE_ID);
}

export function emailMessage(): string {
  return translation(EMAIL_MESSAGE_ID);
}

/**
 * The translated text for the field's current validity state, or '' when the
 * browser's own message should stay:
 *
 *   - empty required field            -> 16305 with the field's label
 *   - `pattern` / `minlength` failure -> 16523 (e.g. the password rules)
 *   - malformed `type="email"` value  -> 4207
 *   - the form's own rule, if any     -> whatever the validator returns
 *   - anything else (url, number…)    -> '' - the browser message is already
 *                                         localized by the browser itself
 *
 * `validity` flags other than `customError` are computed by the browser from
 * the attributes alone, so a message set earlier does not affect the answer.
 */
export function validationMessage(field: ValidatableField, validator?: FieldValidator): string {
  const { validity } = field;
  if (validity.valueMissing) return requiredFieldMessage(fieldLabel(field));
  if (validity.patternMismatch || validity.tooShort) return formatMessage();
  if (validity.typeMismatch && field.type === 'email') return emailMessage();
  return validator ? validator(field) : '';
}

/**
 * Sets the translated message on the field (see `validationMessage`).
 *
 * A custom message keeps the field invalid on its own, so after this call
 * `validity.valid` is false exactly when the message is non-empty OR a native
 * constraint without a translation (e.g. a url field) fails.
 */
export function applyValidationMessage(field: ValidatableField, validator?: FieldValidator) {
  field.setCustomValidity(validationMessage(field, validator));
}
